/**
 * MCP HTTP Client for the Agion Admin MCP server.
 * Connects via APISIX forward-auth (same session as admin-ui).
 * 
 * Protocol: JSON-RPC 2.0 over HTTP POST /mcp
 * Auth: Session cookie (forwarded by APISIX)
 */

import { API_CONFIG } from '@/lib/api-config'
import { getConfig } from '@/shared/config/runtime-env'

export function isAdminMCPEnabled(): boolean {
  return (getConfig('VITE_ADMIN_MCP_ENABLED', 'false') ?? 'false').toLowerCase() === 'true'
}

export interface MCPTool {
  name: string
  description: string
  inputSchema: Record<string, unknown>
  annotations?: {
    minRole?: string
    adminRoute?: string
  }
}

export interface MCPConfirmAction {
  /** Short action-word label for the button ("Create", "Delete", "Apply") */
  label: string
  /** Tool name to invoke on click */
  tool: string
  /** Arguments passed verbatim to the tools/call — usually just {preview_id, confirm: true} */
  arguments: Record<string, unknown>
  /** Visual style hint: "primary" (default) or "danger" for destructive actions */
  style?: 'primary' | 'danger'
  /** Optional short description rendered below the button */
  description?: string
}

export interface MCPResultMeta {
  navigateTo?: string
  deepLink?: string
  secureForm?: {
    title: string
    endpoint: string
    method: string
    fields: Array<{
      name: string
      label: string
      type: 'text' | 'password' | 'url'
      required: boolean
      placeholder?: string
    }>
    hidden?: Record<string, string>
  }
  /**
   * confirmAction tells the chat-ui to render a click-to-execute
   * button next to the preview. Clicking it re-invokes the named
   * tool with the stored arguments, bypassing the "re-type everything
   * or say yes" confirmation flow.
   */
  confirmAction?: MCPConfirmAction
  /**
   * rulePreview carries a structured governance rule for the chat
   * to render as a RuleSentence-style card (matching the Policy
   * Center display). When present, the chat renders this instead
   * of the plain markdown preview.
   */
  rulePreview?: MCPRulePreview
  refresh?: string[]
  toast?: string
  classification?: string
  /**
   * summary is a short description of what this tool response did.
   * Used by the conversation compaction logic to compress old turns
   * into a condensed context summary, keeping the context window small.
   */
  summary?: string
}

export interface MCPRulePreview {
  name: string
  description?: string
  conditionField: string
  conditionOperator: string
  conditionValue: unknown
  action: string
  maxTrustTier: number
  andConditions?: Array<{
    field: string
    operator: string
    value: unknown
    logicalOperator?: 'and' | 'or'
  }>
  scope?: string
  priority?: number
  enabled: boolean
}

export interface MCPToolResult {
  content: Array<{ type: string; text: string }>
  isError?: boolean
  _meta?: MCPResultMeta
}

let requestId = 0

class MCPClient {
  private baseUrl: string
  private initialized = false
  // userIntent is the natural-language text from the most recent user
  // message. The assistant hook (useAssistant.sendMessage) sets this
  // immediately before routing the message, and the rpc() loop attaches
  // it as the X-Intent-Topic header. The admin-mcp server reads that
  // header into UserContext.IntentTopic → frame.Intent.Topic, which the
  // dispatcher threads into the precedent narrative. Stays empty for
  // speculative tool calls (page_context fetches, init handshakes) so
  // those don't pollute the learning loop with no-op intents. See
  // plans/architecture/2026-05-25-learnings-from-audit-foundation.md.
  private userIntent = ''

  constructor() {
    this.baseUrl = API_CONFIG.services.unifiedGateway || ''
  }

  /** Set the natural-language intent for the next user-initiated tool calls. */
  setUserIntent(text: string): void {
    this.userIntent = text.trim()
  }

  /** Clear the intent so subsequent speculative calls don't inherit it. */
  clearUserIntent(): void {
    this.userIntent = ''
  }

  private async rpc(method: string, params?: unknown): Promise<unknown> {
    if (!isAdminMCPEnabled()) {
      throw new Error('Admin MCP is disabled')
    }

    const id = ++requestId
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Page-Context': window.location.pathname,
    }
    if (this.userIntent) {
      headers['X-Intent-Topic'] = this.userIntent
    }
    const response = await fetch(`${this.baseUrl}/mcp`, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify({
        jsonrpc: '2.0',
        id,
        method,
        params: params || {},
      }),
    })

    if (!response.ok) {
      throw new Error(`MCP request failed: ${response.status}`)
    }

    const result = await response.json()
    if (result.error) {
      throw new Error(result.error.message || 'MCP error')
    }
    return result.result
  }

  async initialize(): Promise<void> {
    if (this.initialized) return
    await this.rpc('initialize', {
      protocolVersion: '2024-11-05',
      capabilities: {},
      clientInfo: { name: 'agion-admin-ui', version: '1.0.0' },
    })
    // Send notification (no response expected)
    fetch(`${this.baseUrl}/mcp`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ jsonrpc: '2.0', method: 'notifications/initialized' }),
    }).catch(() => {}) // fire and forget
    this.initialized = true
  }

  async listTools(): Promise<MCPTool[]> {
    await this.initialize()
    const result = await this.rpc('tools/list') as { tools: MCPTool[] }
    return result.tools
  }

  async callTool(name: string, args: Record<string, unknown> = {}): Promise<MCPToolResult> {
    await this.initialize()
    return await this.rpc('tools/call', { name, arguments: args }) as MCPToolResult
  }
}

export const mcpClient = new MCPClient()
